// frontend/src/components/ui/Button.tsx
import React from 'react';
import { cn } from '@/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'warning' | 'success';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  rounded?: 'sm' | 'md' | 'lg' | 'full';
  responsive?: boolean; // Automatically adjust size based on screen size
}

const buttonVariants = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 focus:ring-blue-500 dark:bg-blue-600 dark:hover:bg-blue-700',
  secondary: 'bg-gray-600 text-white hover:bg-gray-700 active:bg-gray-800 focus:ring-gray-500 dark:bg-gray-600 dark:hover:bg-gray-700',
  outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
  ghost: 'text-gray-700 hover:bg-gray-100 active:bg-gray-200 focus:ring-blue-500 dark:text-gray-300 dark:hover:bg-gray-800 dark:active:bg-gray-700',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus:ring-red-500 dark:bg-red-600 dark:hover:bg-red-700',
  warning: 'bg-orange-600 text-white hover:bg-orange-700 active:bg-orange-800 focus:ring-orange-500 dark:bg-orange-600 dark:hover:bg-orange-700',
  success: 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800 focus:ring-green-500 dark:bg-green-600 dark:hover:bg-green-700',
};

const sizeVariants = {
  xs: 'px-2 py-1 text-xs font-medium min-h-[24px]',
  sm: 'px-3 py-1.5 text-sm font-medium min-h-[32px]',
  md: 'px-4 py-2 text-sm font-medium min-h-[40px]',
  lg: 'px-6 py-3 text-base font-medium min-h-[48px]',
  xl: 'px-8 py-4 text-lg font-medium min-h-[56px]',
};

// Responsive size variants - automatically adjust on different screen sizes
const responsiveSizeVariants = {
  xs: 'px-2 py-1 text-xs sm:px-2 sm:py-1 sm:text-xs font-medium min-h-[24px]',
  sm: 'px-2 py-1.5 text-xs sm:px-3 sm:py-1.5 sm:text-sm font-medium min-h-[28px] sm:min-h-[32px]',
  md: 'px-3 py-2 text-sm sm:px-4 sm:py-2 sm:text-sm font-medium min-h-[36px] sm:min-h-[40px]',
  lg: 'px-4 py-2.5 text-sm sm:px-6 sm:py-3 sm:text-base font-medium min-h-[40px] sm:min-h-[48px]',
  xl: 'px-6 py-3 text-base sm:px-8 sm:py-4 sm:text-lg font-medium min-h-[48px] sm:min-h-[56px]',
};

const roundedVariants = {
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  rounded = 'md',
  responsive = false,
  disabled,
  children,
  className,
  type = 'button',
  ...props
}) => {
  const isDisabled = disabled || loading;

  // Choose size variant based on responsive prop
  const currentSizeVariants = responsive ? responsiveSizeVariants : sizeVariants;

  const buttonContent = (
    <>
      {loading && (
        <svg className="w-3 h-3 sm:w-4 sm:h-4 animate-spin mr-1.5 sm:mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {!loading && leftIcon && <span className="mr-1.5 sm:mr-2 flex-shrink-0">{leftIcon}</span>}
      <span className="truncate min-w-0">{children}</span>
      {!loading && rightIcon && <span className="ml-1.5 sm:ml-2 flex-shrink-0">{rightIcon}</span>}
    </>
  );

  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center font-medium',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',
        'transition-all duration-200',
        // Touch-friendly hover states for mobile
        'hover:scale-105 active:scale-95',
        // Enhanced touch targets for mobile
        'touch-manipulation',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
        'select-none',
        // Prevent text selection on mobile
        'user-select-none',
        buttonVariants[variant],
        currentSizeVariants[size],
        roundedVariants[rounded],
        fullWidth ? 'w-full' : '',
        className
      )}
      disabled={isDisabled}
      {...props}
    >
      {buttonContent}
    </button>
  );
};